import { Drawer } from '@/components';
import { usePriorIdFromLineage } from '@/features/project/_hooks/project';
import { useProject } from '@/features/project/_hooks/project';
import { isBefore2025 } from '@/features/project/_utils/project';
import {
  useCreateFundBusiness,
  useFundBusinesses,
  useUpdateFundBusiness,
} from '@/features/project/projects/detail/fund-bussiness/_hooks/fund-business';
import type { FundBusinessForInvestmentUpsertFormSchema } from '@/features/project/projects/detail/fund-bussiness/_model/fund-business';
import { getInitialPerformancesDataForInvestment } from '@/features/project/projects/detail/fund-bussiness/_utils/fund-business';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { FundBusinessForInvestmentForm } from './FundBusinessForInvestmentForm';

export const FundBusinessForInvestmentFormDrawer: FC = () => {
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { id: projectId } = useId();
  const project = useProject(projectId);
  const priorProjectId =
    usePriorIdFromLineage({
      params: {
        fiscalYear: project.data?.fiscalYear,
        lineageId: project.data?.lineageId ?? '',
      },
    }).data?.priorProjectId ?? '';
  const fundBusinesses = useFundBusinesses({ projectId });
  const priorFundBusinesses = useFundBusinesses({ projectId: priorProjectId });
  const { mutateAsync: createFundBusiness } = useCreateFundBusiness(projectId);
  const { mutateAsync: updateFundBusiness } = useUpdateFundBusiness(projectId);

  const isDrawerOpen =
    drawerState.open &&
    drawerState.type === 'fund-business' &&
    project.data?.fundAdditionalAttributes?.fundSheetType === 'investment';

  const isEdit = isDrawerOpen && drawerState.isEditing;

  const fundBusiness = isEdit
    ? fundBusinesses.data?.find((v) => v.id === drawerState.fundBusinessId)
    : undefined;
  const priorFundBusiness = priorFundBusinesses.data?.find(
    (priorFundBusiness) => priorFundBusiness.id === fundBusiness?.priorId,
  );

  const initialValues = useMemo(() => {
    const performances =
      fundBusiness?.performances ?? getInitialPerformancesDataForInvestment();

    return {
      businessForm: fundBusiness?.businessForm,
      title: fundBusiness?.title ?? null,
      performances: performances.map((performance, performanceIndex) => {
        if (!isBefore2025(project.data) && performance) {
          if (performanceIndex === 4) {
            return {
              ...performance,
              yearAmountInfos:
                priorFundBusiness?.performances[2]?.yearAmountInfos ??
                performance.yearAmountInfos,
            };
          }
          if (performanceIndex === 5) {
            return {
              ...performance,
              yearAmountInfos:
                priorFundBusiness?.performances[3]?.yearAmountInfos ??
                performance.yearAmountInfos,
            };
          }
        }
        return performance;
      }),
    };
  }, [fundBusiness, project.data, priorFundBusiness]);

  const closeDrawer = () => {
    setDrawerState({ open: false });
  };

  const handleSubmit = async (
    values: z.infer<typeof FundBusinessForInvestmentUpsertFormSchema>,
  ) => {
    try {
      fundBusiness
        ? await updateFundBusiness({ fundBusinessId: fundBusiness.id, values })
        : await createFundBusiness(values);

      await Promise.all([
        openMessage(
          'success',
          fundBusiness
            ? t('message-update-completed')
            : t('message-create-completed'),
        ),
        (async () => {
          closeDrawer();
        })(),
      ]);
    } catch {
      await openMessage(
        'error',
        fundBusiness ? t('message-update-failed') : t('message-create-failed'),
      );
    }
  };

  return (
    <Drawer
      size="large"
      destroyOnClose
      open={isDrawerOpen}
      onClose={closeDrawer}
    >
      <FundBusinessForInvestmentForm
        type={isEdit ? 'edit' : 'new'}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      />
    </Drawer>
  );
};
